import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Trash2, FileSpreadsheet, List as ListIcon, Grid3x3, PackageOpen, PackagePlus, Upload } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { photoFull } from "@/lib/photoUrl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { SampleProductPicker } from "@/components/sample/SampleProductPicker";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** 寄样产品清单 — products of one 寄样清单, list / grid view, auto-saving
 *  备注 / 数量 and an inline-editable 客户名称. */
export default function SampleListDetail() {
  const { id } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [customers, setCustomers] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const timers = useRef({});

  /** 2026-06 · 直接上传照片建条目（手机相册/相机或电脑文件，支持多选）。 */
  const uploadPhotos = async (files) => {
    const list = [...(files || [])];
    if (!list.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of list) {
      try {
        const form = new FormData();
        form.append("file", file);
        form.append("name", file.name.replace(/\.[^.]+$/, ""));
        await api.post(`/sample-lists/${id}/items/upload`, form,
          { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
    setUploading(false);
    if (ok) {
      toast.success(t("sample.uploadedToast").replace("{n}", ok));
      load();
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sample-lists/${id}/items`);
      setDetail(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => {
    load();
    api.get("/customers").then(({ data }) => setCustomers((data || []).map((c) => c.name).filter(Boolean))).catch(() => {});
  }, [load]);

  const patchItem = (itemId, patch) => {
    setDetail((d) => ({ ...d, items: d.items.map((it) => (it.id === itemId ? { ...it, ...patch } : it)) }));
    clearTimeout(timers.current[itemId]);
    timers.current[itemId] = setTimeout(async () => {
      try { await api.patch(`/sample-lists/${id}/items/${itemId}`, patch); }
      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    }, 700);
  };

  const patchCustomer = (value) => {
    setDetail((d) => ({ ...d, customer_name: value }));
    clearTimeout(timers.current.__cust);
    timers.current.__cust = setTimeout(async () => {
      try { await api.patch(`/sample-lists/${id}`, { customer_name: value }); }
      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    }, 700);
  };

  const patchReceipt = (value) => {
    setDetail((d) => ({ ...d, receipt_no: value }));
    clearTimeout(timers.current.__receipt);
    timers.current.__receipt = setTimeout(async () => {
      if (!value.trim()) return;
      try { await api.patch(`/sample-lists/${id}`, { receipt_no: value }); }
      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    }, 700);
  };

  const removeItem = async (itemId) => {
    try {
      await api.delete(`/sample-lists/${id}/items/${itemId}`);
      setDetail((d) => ({ ...d, items: d.items.filter((it) => it.id !== itemId) }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading || !detail) {
    return <p className="text-center py-16"><Loader2 className="inline animate-spin text-muted-foreground" /></p>;
  }

  return (
    <div className="animate-fade-in-up space-y-4" data-testid="sample-detail-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate("/sample-lists")}
              data-testid="sd-back"><ArrowLeft size={15} /></Button>
            <h1 className="font-heading text-base font-bold tracking-tight flex items-center gap-2">
              <PackageOpen size={17} className="text-brand" /> {detail.name}
            </h1>
          </div>
          <p className="text-[11px] font-mono text-muted-foreground pl-9">
            {t("sample.count").replace("{n}", detail.items.length)} · {detail.created_by} · {(detail.created_at || "").slice(0, 10)}
          </p>
          <div className="flex items-center gap-2 pl-9 pt-1">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("sample.customer")}</span>
            <Input value={detail.customer_name || ""} list="sd-customers" onChange={(e) => patchCustomer(e.target.value)}
              placeholder={t("sample.customerPlaceholder")} className="h-7 text-xs w-[240px]" data-testid="sd-customer" />
            <datalist id="sd-customers">{customers.map((c) => <option key={c} value={c} />)}</datalist>
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground ml-2">{t("sample.receiptNo")}</span>
            <Input value={detail.receipt_no || ""} onChange={(e) => patchReceipt(e.target.value)}
              placeholder={t("sample.receiptNoPlaceholder")} className="h-7 text-xs w-[180px] font-mono" data-testid="sd-receipt" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* 2026-06 · 从产品一览表选择 + 直接上传照片（手机或电脑） */}
          {detail.can_manage !== false && (
            <>
              <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={() => setPickerOpen(true)}
                data-testid="sd-pick-products"><PackagePlus size={13} /> {t("sample.pickProducts")}</Button>
              <label className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-border text-xs font-medium cursor-pointer hover:bg-secondary transition-colors"
                data-testid="sd-upload-photo">
                <input type="file" accept="image/*" multiple className="hidden"
                  onChange={(e) => { uploadPhotos(e.target.files); e.target.value = ""; }} />
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {t("sample.uploadPhoto")}
              </label>
            </>
          )}
          <div className="flex items-center gap-1 border border-border rounded-md p-0.5">
            <Button variant={view === "list" ? "secondary" : "ghost"} size="icon" className="h-7 w-7"
              onClick={() => setView("list")} data-testid="sd-view-list"><ListIcon size={13} /></Button>
            <Button variant={view === "grid" ? "secondary" : "ghost"} size="icon" className="h-7 w-7"
              onClick={() => setView("grid")} data-testid="sd-view-grid"><Grid3x3 size={13} /></Button>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.open(`${BACKEND}/api/sample-lists/${id}/export`, "_blank")}
            data-testid="sd-export"><FileSpreadsheet size={13} /> {t("sample.export")}</Button>
        </div>
      </div>

      {detail.items.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-12" data-testid="sd-empty">{t("sample.noItems")}</p>
      )}

      {view === "list" && detail.items.length > 0 && (
        <div className="bg-card border border-border rounded-md overflow-hidden">
          <table className="w-full text-xs table-fixed" data-testid="sd-table">
            <colgroup>
              <col className="w-[64px]" />
              <col className="w-[120px]" />
              <col className="w-[30%]" />
              <col className="w-[70px]" />
              <col className="w-[42%]" />
              <col className="w-[44px]" />
            </colgroup>
            <thead className="bg-secondary/60">
              <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-2 py-2"></th>
                <th className="px-2 py-2 text-left">{t("sample.itemNo")}</th>
                <th className="px-2 py-2 text-left">{t("sample.name")}</th>
                <th className="px-2 py-2 text-left">{t("sample.qty")}</th>
                <th className="px-2 py-2 text-left">{t("sample.remark")}</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {detail.items.map((it) => (
                <tr key={it.id} className="border-t border-border/60" data-testid={`sd-item-${it.item_number}`}>
                  <td className="px-2 py-1.5">
                    {it.photo_url
                      ? <img src={photoFull(it.photo_url)} alt="" className="w-11 h-11 object-contain bg-white rounded border border-border" />
                      : <div className="w-11 h-11 rounded bg-secondary" />}
                  </td>
                  <td className="px-2 py-1.5 font-mono font-semibold">{it.item_number}</td>
                  <td className="px-2 py-1.5" title={it.name}>
                    <span className="line-clamp-2 leading-snug">{it.name}</span>
                  </td>
                  <td className="px-2 py-1.5">
                    <Input type="number" min={0} value={it.qty}
                      onChange={(e) => patchItem(it.id, { qty: Number(e.target.value) })}
                      className="h-7 text-xs w-full" data-testid={`sd-qty-${it.item_number}`} />
                  </td>
                  <td className="px-2 py-1.5">
                    <Textarea value={it.remark} rows={2} placeholder={t("sample.remarkPlaceholder")}
                      onChange={(e) => patchItem(it.id, { remark: e.target.value })}
                      className="text-xs min-h-[42px] py-1 resize-y leading-snug"
                      data-testid={`sd-remark-${it.item_number}`} />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(it.id)}
                      data-testid={`sd-remove-${it.item_number}`}><Trash2 size={12} className="text-destructive" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === "grid" && detail.items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="sd-grid">
          {detail.items.map((it) => (
            <div key={it.id} className="border border-border rounded-md bg-card p-2 space-y-1.5" data-testid={`sd-card-${it.item_number}`}>
              <div className="aspect-square rounded bg-white dark:bg-secondary/40 overflow-hidden flex items-center justify-center p-1">
                {it.photo_url && <img src={photoFull(it.photo_url)} alt="" loading="lazy"
                  className="max-w-full max-h-full object-contain" />}
              </div>
              <p className="text-xs font-mono font-semibold">{it.item_number}</p>
              <p className="text-[11px] text-muted-foreground line-clamp-2" title={it.name}>{it.name}</p>
              <div className="flex items-start gap-1">
                <Input type="number" min={0} value={it.qty}
                  onChange={(e) => patchItem(it.id, { qty: Number(e.target.value) })} className="h-7 text-xs w-14" />
                <Textarea value={it.remark} rows={2} placeholder={t("sample.remarkPlaceholder")}
                  onChange={(e) => patchItem(it.id, { remark: e.target.value })}
                  className="text-xs min-h-[42px] py-1 resize-y leading-snug" />
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => removeItem(it.id)}>
                  <Trash2 size={12} className="text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <SampleProductPicker open={pickerOpen} onOpenChange={setPickerOpen} listId={id} onAdded={load} />
    </div>
  );
}
